import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../hooks/useAuth';
import usePermissions from '../hooks/usePermissions';
import Swal from 'sweetalert2';

const StandardDocuments = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { canDelete } = usePermissions();
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  
  const [formData, setFormData] = useState({
    documentName: '',
    documentType: '',
    companyId: '',
    employeeId: '',
    terminationDate: '', // Geri ekledik
    note: '',
    senderName: '',
    employmentLeftoverVacationDays: '',
    employmentStartDate: '',
    absenceStartDate: '',
    absenceDeadlineDate: ''
  });

  // Fetch companies for selection
  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/companies');
      return response.data;
    }
  });

  // Fetch employees for selection (filtered by selected company)
  const { data: employees = [] } = useQuery({
    queryKey: ['employees', formData.companyId],
    queryFn: async () => {
      if (!formData.companyId) return [];
      const response = await api.get('/employees');
      return response.data.filter(emp => emp.companyId._id === formData.companyId || emp.companyId === formData.companyId);
    },
    enabled: !!formData.companyId
  });

  // Fetch standard documents
  const { data: documentsData, isLoading } = useQuery({
    queryKey: ['standard-documents'],
    queryFn: async () => {
      const response = await api.get('/standard-documents');
      return response.data;
    }
  });

  const documents = documentsData?.documents || [];

  // Save document mutation
  const saveMutation = useMutation({
    mutationFn: async (data) => {
        const payload = {
          companyId: data.companyId,
          employeeId: data.employeeId._id || data.employeeId, // Handle both object and string
          documentType: data.documentType,
          documentName: data.documentName,
          terminationDate: data.terminationDate,
          note: data.note,
          senderName: data.senderName,
          employmentLeftoverVacationDays: data.employmentLeftoverVacationDays,
          employmentStartDate: data.employmentStartDate,
          absenceStartDate: data.absenceStartDate,
          absenceDeadlineDate: data.absenceDeadlineDate
        };
      
      console.log('🔍 DEBUG: Sending payload:', payload);
      console.log('🔍 DEBUG: Original data:', data);

      if (editingDocument) {
        const response = await api.put(`/standard-documents/${editingDocument._id}`, payload);
        return response.data;
      } else {
        const response = await api.post('/standard-documents', payload);
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['standard-documents']);
      setShowForm(false);
      setEditingDocument(null);
      resetForm();
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: editingDocument ? t('documents.documentUpdatedSuccess') : t('documents.documentCreatedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('documents.documentSaveFailed'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  // Delete document mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/standard-documents/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['standard-documents']);
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('documents.documentDeletedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('documents.documentDeleteFailed'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  const documentTypes = [
    { value: 'arbeitgeberbescheinigung', label: t('documents.arbeitgeberbescheinigung') },
    { value: 'außerordentliche_kündigung', label: t('documents.außerordentliche_kündigung') },
    { value: 'kündigung_betriebliche_gründe', label: t('documents.kündigung_betriebliche_gründe') },
    { value: 'beendigung_vertragsablauf', label: t('documents.beendigung_vertragsablauf') },
    { value: 'fristgerechte_kündigung', label: t('documents.fristgerechte_kündigung') },
    { value: 'arbeitsaufforderung', label: t('documents.arbeitsaufforderung') }
  ];

  const resetForm = () => {
    setFormData({
      documentName: '',
      documentType: '',
      companyId: '',
      employeeId: '',
      terminationDate: '', // Geri ekledik
      note: '',
      senderName: '',
      employmentLeftoverVacationDays: '',
      employmentStartDate: '',
      absenceStartDate: '',
      absenceDeadlineDate: ''
    });
    setSelectedCompany('');
    setSelectedDocumentType('');
    setEditingDocument(null);
  };

  const handleDocumentTypeChange = (type) => {
    setSelectedDocumentType(type);
    setFormData(prev => ({
      ...prev,
      documentType: type
    }));
  };

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'companyId') {
      // Company değiştiğinde employee'ı sıfırla
      setFormData(prev => ({
        ...prev,
        [name]: value,
        employeeId: ''
      }));
    } else if (name === 'employeeId') {
      // Employee seçildiğinde document name'i otomatik oluştur
      const selectedEmployee = employees.find(emp => emp._id === value);
      if (selectedEmployee) {
        const documentName = `${selectedEmployee.title} ${selectedEmployee.firstName} ${selectedEmployee.lastName} - ${formData.documentType || 'Arbeitgeberbescheinigung'}`;
        setFormData(prev => ({
          ...prev,
          [name]: value,
          documentName: documentName
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else if (name === 'documentType') {
      // Document type seçildiğinde document name'i güncelle
      const selectedEmployee = employees.find(emp => emp._id === formData.employeeId);
      if (selectedEmployee) {
        const documentName = `${selectedEmployee.title} ${selectedEmployee.firstName} ${selectedEmployee.lastName} - ${value}`;
        setFormData(prev => ({
          ...prev,
          [name]: value,
          documentName: documentName
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setSelectedDocumentType(document.documentType);
    setSelectedCompany(document.companyId._id);
    setFormData({
      documentName: document.documentName,
      documentType: document.documentType,
      companyId: document.companyId._id,
      employeeId: document.employeeId._id || document.employeeId || '', // Handle both object and string
      terminationDate: document.employeeId?.employmentEndDate ? new Date(document.employeeId.employmentEndDate).toISOString().split('T')[0] : '',
      note: document.note || '',
      senderName: document.senderName || '',
      employmentLeftoverVacationDays: document.employmentLeftoverVacationDays || '',
      employmentStartDate: document.employmentStartDate || '',
      absenceStartDate: document.absenceStartDate || '',
      absenceDeadlineDate: document.absenceDeadlineDate || ''
    });
    setShowForm(true);
  };

  const handleDeleteDocument = (id) => {
    Swal.fire({
      title: t('documents.confirmDelete'),
      text: t('documents.cannotRevert'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('documents.yesDelete'),
      cancelButtonText: t('common.cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        deleteMutation.mutate(id);
      }
    });
  };

  const handleSaveDocument = () => {
    if (!formData.documentType || !formData.companyId || !formData.employeeId || !formData.documentName) {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: t('documents.pleaseFillRequiredFields'),
        confirmButtonText: t('common.ok')
      });
      return;
    }
    
    saveMutation.mutate(formData);
  };

  const handleGeneratePDF = async (documentId) => {
    try {
      // Show options modal first
      const result = await Swal.fire({
        title: t('documents.selectPDFType'),
        text: t('documents.selectPDFTypeText'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: t('documents.withStamp'),
        cancelButtonText: t('documents.withoutStamp'),
        reverseButtons: true,
        customClass: {
          confirmButton: 'bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded mr-2',
          cancelButton: 'bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded'
        }
      });

      console.log('🔍 Frontend DEBUG: SweetAlert2 result:', result);
      console.log('🔍 Frontend DEBUG: result.isConfirmed:', result.isConfirmed);
      console.log('🔍 Frontend DEBUG: result.isDismissed:', result.isDismissed);
      console.log('🔍 Frontend DEBUG: result.dismiss:', result.dismiss);
      
      // User cancelled (X button or ESC)
      if (result.isDismissed && result.dismiss !== 'cancel') {
        console.log('🔍 Frontend DEBUG: User cancelled with X or ESC');
        return;
      }

      // reverseButtons: true olduğu için:
      // - Confirm button (Yeşil) = Stamp'lı = true
      // - Cancel button (Gri) = Stamp'sız = false
      const withSignature = result.isConfirmed; // true for confirm, false for cancel
      
      console.log('🔍 Frontend DEBUG: withSignature calculated:', withSignature);
      console.log('Generating PDF for document:', documentId, 'withSignature:', withSignature);
      const response = await api.post(`/standard-documents/${documentId}/generate-pdf`, {
        withSignature
      });
      console.log('PDF generation response:', response.data);
      const { pdfPath, documentType } = response.data;
      
      // Plesk ortamında API base URL'i kullan
      const apiBaseUrl = process.env.NODE_ENV === 'production'
        ? window.location.origin
        : 'http://localhost:4000';

      const fullPdfUrl = pdfPath.startsWith('http') ? pdfPath : `${apiBaseUrl}${pdfPath}?t=${Date.now()}`;

      // Show PDF in modal like invoices
      const modal = document.createElement('div');
      modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
      modal.innerHTML = `
        <div class="bg-white rounded-lg p-6 max-w-7xl max-h-full overflow-auto w-full mx-4">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">${t(`documents.${documentType}`)} - PDF ${withSignature ? '(İmzalı)' : '(İmzasız)'}</h3>
            <button onclick="this.closest('.fixed').remove()" class="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
          </div>
          <div class="mb-4">
            <object data="${fullPdfUrl}" type="application/pdf" width="100%" height="800px">
              <p>PDF cannot be displayed. <a href="${fullPdfUrl}" target="_blank">Click here to download</a></p>
            </object>
          </div>
          <div class="flex justify-end space-x-2">
            <a href="${fullPdfUrl}" target="_blank" class="btn btn-secondary">
              ${t('common.download')}
            </a>
            <button onclick="this.closest('.fixed').remove()" class="btn btn-primary">
              ${t('common.close')}
            </button>
          </div>
        </div>
      `;
      
      document.body.appendChild(modal);
      
      // Remove modal when clicking outside
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.remove();
        }
      });
      
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('documents.pdfGenerationFailed'),
        confirmButtonText: t('common.ok')
      });
    }
  };

  const handleSendMail = async (document) => {
    // Get employee email
    const employeeEmail = document.employeeId?.email || '';
    const employeeName = `${document.employeeId?.firstName || ''} ${document.employeeId?.lastName || ''}`.trim();

    // Fetch SMTP options
    const smtpOptionsResponse = await api.get('/send-mail/smtp-options');
    const smtpOptions = smtpOptionsResponse.data;

    // Fetch template options
    const templateOptionsResponse = await api.get('/send-mail/template-options');
    const templateOptions = templateOptionsResponse.data;

    // Show email form modal
    const result = await Swal.fire({
      title: t('documents.sendMail'),
      html: `
        <div class="text-left" style="max-height: 70vh; overflow-y: auto; padding: 20px;">
          <!-- SMTP Selection -->
          <div style="margin-bottom: 20px;">
            <label style="display: block; font-weight: 600; margin-bottom: 8px; color: #374151;">${t('documents.selectSmtp')} *</label>
            <select id="smtpId" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px;">
              <option value="">${t('documents.chooseSmtp')}</option>
              ${smtpOptions.map(smtp => `
                <option value="${smtp._id}">${smtp.name} (${smtp.fromEmail}) ${smtp.isDefault ? '(Default)' : ''}</option>
              `).join('')}
            </select>
          </div>

          <!-- Email Recipient -->
          <div style="margin-bottom: 20px;">
            <label style="display: block; font-weight: 600; margin-bottom: 8px; color: #374151;">${t('documents.recipientEmail')} *</label>
            <input id="recipientEmail" type="email" value="${employeeEmail}" placeholder="${t('documents.enterEmail')}" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px;">
            <small style="display: block; margin-top: 4px; color: #6b7280; font-size: 12px;">${t('documents.defaultEmployeeEmail')}</small>
          </div>

          <!-- Recipient Name -->
          <div style="margin-bottom: 20px;">
            <label style="display: block; font-weight: 600; margin-bottom: 8px; color: #374151;">${t('documents.recipientName')}</label>
            <input id="recipientName" type="text" value="${employeeName}" placeholder="${t('documents.enterName')}" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px;">
          </div>

          <!-- Template or Manual -->
          <div style="margin-bottom: 20px; padding: 15px; background-color: #f9fafb; border-radius: 6px; border: 1px solid #e5e7eb;">
            <label style="display: flex; align-items: center; cursor: pointer;">
              <input type="checkbox" id="useTemplate" style="width: 18px; height: 18px; margin-right: 10px; cursor: pointer;">
              <span style="font-weight: 600; color: #374151;">${t('documents.useEmailTemplate')}</span>
            </label>
          </div>

          <!-- Template Selection -->
          <div id="templateSection" style="display: none; margin-bottom: 20px;">
            <label style="display: block; font-weight: 600; margin-bottom: 8px; color: #374151;">${t('documents.selectTemplate')} *</label>
            <select id="templateId" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px;">
              <option value="">${t('documents.chooseTemplate')}</option>
              ${templateOptions.map(template => `
                <option value="${template._id}">${template.name} ${template.isDefault ? '(Default)' : ''}</option>
              `).join('')}
            </select>
          </div>

          <!-- Manual Subject -->
          <div id="manualSubjectSection" style="margin-bottom: 20px;">
            <label style="display: block; font-weight: 600; margin-bottom: 8px; color: #374151;">${t('documents.emailSubject')} *</label>
            <input id="emailSubject" type="text" placeholder="${t('documents.enterSubject')}" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px;">
          </div>

          <!-- Manual Body -->
          <div id="manualBodySection" style="margin-bottom: 20px;">
            <label style="display: block; font-weight: 600; margin-bottom: 8px; color: #374151;">${t('documents.emailBody')} *</label>
            <textarea id="emailBody" rows="6" placeholder="${t('documents.enterEmailBody')}" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; resize: vertical; font-family: inherit;"></textarea>
          </div>
        </div>
      `,
      width: '700px',
      showCancelButton: true,
      confirmButtonText: t('documents.send'),
      cancelButtonText: t('common.cancel'),
      customClass: {
        popup: 'rounded-lg',
        confirmButton: 'bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-md',
        cancelButton: 'bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-md mr-2'
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        const useTemplateCheckbox = popup.querySelector('#useTemplate');
        const templateSection = popup.querySelector('#templateSection');
        const manualSubjectSection = popup.querySelector('#manualSubjectSection');
        const manualBodySection = popup.querySelector('#manualBodySection');
        const emailSubjectInput = popup.querySelector('#emailSubject');
        const emailBodyInput = popup.querySelector('#emailBody');

        useTemplateCheckbox.addEventListener('change', (e) => {
          if (e.target.checked) {
            // Show template section
            templateSection.style.display = 'block';
            // Hide and disable manual sections
            manualSubjectSection.style.display = 'none';
            manualBodySection.style.display = 'none';
            emailSubjectInput.disabled = true;
            emailBodyInput.disabled = true;
            emailSubjectInput.value = '';
            emailBodyInput.value = '';
          } else {
            // Hide template section
            templateSection.style.display = 'none';
            // Show and enable manual sections
            manualSubjectSection.style.display = 'block';
            manualBodySection.style.display = 'block';
            emailSubjectInput.disabled = false;
            emailBodyInput.disabled = false;
          }
        });
      },
      preConfirm: () => {
        const popup = Swal.getPopup();
        const smtpId = popup.querySelector('#smtpId').value;
        const recipientEmail = popup.querySelector('#recipientEmail').value;
        const recipientName = popup.querySelector('#recipientName').value;
        const useTemplate = popup.querySelector('#useTemplate').checked;
        const templateId = popup.querySelector('#templateId').value;
        const emailSubject = popup.querySelector('#emailSubject').value;
        const emailBody = popup.querySelector('#emailBody').value;

        if (!smtpId) {
          Swal.showValidationMessage(t('documents.smtpRequired'));
          return false;
        }

        if (!recipientEmail) {
          Swal.showValidationMessage(t('documents.emailRequired'));
          return false;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
          Swal.showValidationMessage(t('documents.invalidEmail'));
          return false;
        }

        if (useTemplate && !templateId) {
          Swal.showValidationMessage(t('documents.templateRequired'));
          return false;
        }

        if (!useTemplate && !emailSubject) {
          Swal.showValidationMessage(t('documents.subjectRequired'));
          return false;
        }

        if (!useTemplate && !emailBody) {
          Swal.showValidationMessage(t('documents.bodyRequired'));
          return false;
        }

        return {
          smtpId,
          recipientEmail,
          recipientName,
          useTemplate,
          templateId,
          emailSubject,
          emailBody
        };
      }
    });

    if (result.isConfirmed && result.value) {
      try {
        const payload = {
          smtpId: result.value.smtpId,
          toEmail: result.value.recipientEmail,
          toName: result.value.recipientName,
          documentId: document._id
        };

        if (result.value.useTemplate) {
          payload.templateId = result.value.templateId;
        } else {
          payload.subject = result.value.emailSubject;
          payload.body = result.value.emailBody;
        }

        await api.post(`/standard-documents/${document._id}/send-mail`, payload);

        Swal.fire({
          icon: 'success',
          title: t('common.success'),
          text: t('documents.emailSentSuccess'),
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: t('common.error'),
          text: error.response?.data?.message || t('documents.emailSendFailed'),
          confirmButtonText: t('common.ok')
        });
      }
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {t('documents.title')}
        </h1>
        <p className="text-gray-600">
          {t('documents.description')}
        </p>
      </div>

      {!showForm ? (
        <>
          {/* Documents List */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                {t('documents.documentsList')}
              </h2>
              <button
                onClick={() => {
                  setSelectedDocumentType('');
                  setShowForm(true);
                }}
                className="btn btn-primary"
              >
                {t('documents.createDocument')}
              </button>
            </div>

            {isLoading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">{t('common.loading')}</div>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 mb-4">{t('documents.noDocuments')}</div>
                <button
                  onClick={() => {
                    setSelectedDocumentType('');
                    setShowForm(true);
                  }}
                  className="btn btn-primary"
                >
                  {t('documents.createFirstDocument')}
                </button>
              </div>
            ) : (
              <div className="card">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('documents.documentName')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('documents.employeeName')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('documents.company')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('common.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {documents.map((document) => (
                        <tr key={document._id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {document.documentName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {document.employeeId ? 
                              `${document.employeeId.title || ''} ${document.employeeId.firstName || ''} ${document.employeeId.lastName || ''}`.trim() || 'N/A'
                              : 'N/A'
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {document.companyId?.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditDocument(document)}
                                className="text-blue-600 hover:text-blue-500"
                              >
                                {t('common.edit')}
                              </button>
                              <button
                                onClick={() => handleGeneratePDF(document._id)}
                                className="text-green-600 hover:text-green-500"
                              >
                                {t('documents.generatePDF')}
                              </button>
                              <button
                                onClick={() => handleSendMail(document)}
                                className="text-purple-600 hover:text-purple-500"
                              >
                                {t('documents.sendMail')}
                              </button>
                              {canDelete('standardDocuments') && (
                                <button
                                  onClick={() => handleDeleteDocument(document._id)}
                                  className="text-red-600 hover:text-red-500"
                                >
                                  {t('common.delete')}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

        </>
      ) : !selectedDocumentType ? (
        <div className="max-w-4xl">
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-900">
                {t('documents.selectDocumentType')}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingDocument(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                {t('common.back')}
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documentTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => handleDocumentTypeChange(type.value)}
                  className="p-4 border border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">📄</div>
                    <h3 className="font-medium text-gray-900">{type.label}</h3>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-4xl">
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-900">
                {t(`documents.${selectedDocumentType}`)}
              </h2>
              <button
                onClick={() => {
                  setSelectedDocumentType('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                {t('common.back')}
              </button>
            </div>

            {/* Document Type Selection */}
            <div className="mb-6">
              <label className="label">
                {t('documents.selectDocumentType')} *
              </label>
              <select
                name="documentType"
                value={formData.documentType}
                onChange={handleInputChange}
                className="input"
              >
                <option value="">{t('documents.selectDocumentType')}</option>
                {documentTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Company Selection */}
            <div className="mb-6">
              <label className="label">
                {t('documents.selectCompany')} *
              </label>
              <select
                name="companyId"
                value={formData.companyId}
                onChange={handleInputChange}
                className="input"
              >
                <option value="">{t('documents.chooseCompany')}</option>
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Employee Selection */}
            <div className="mb-6">
              <label className="label">
                {t('documents.selectEmployee')} *
              </label>
              <select
                name="employeeId"
                value={formData.employeeId}
                onChange={handleInputChange}
                className="input"
                disabled={!formData.companyId}
              >
                <option value="">{t('documents.chooseEmployee')}</option>
                {employees.map((employee) => (
                  <option key={employee._id} value={employee._id}>
                    {employee.title} {employee.firstName} {employee.lastName}
                  </option>
                ))}
              </select>
            </div>

            {/* Document Name */}
            <div className="mb-6">
              <label className="label">
                {t('documents.documentName')} *
              </label>
              <input
                type="text"
                name="documentName"
                value={formData.documentName}
                onChange={handleInputChange}
                className="input"
                placeholder={t('documents.documentNamePlaceholder')}
              />
            </div>

      {/* Termination Date - Only for termination documents and if employee doesn't have employmentEndDate */}
      {(formData.documentType === 'außerordentliche_kündigung' || formData.documentType === 'kündigung_betriebliche_gründe' || formData.documentType === 'beendigung_vertragsablauf' || formData.documentType === 'fristgerechte_kündigung') && formData.employeeId && (() => {
        const selectedEmployee = employees.find(emp => 
          (emp._id === formData.employeeId) || 
          (emp._id === formData.employeeId._id)
        );
        
        // If employee doesn't have employmentEndDate, show input
        if (!selectedEmployee?.employmentEndDate) {
          return (
            <div className="mb-6">
              <label className="label">
                {t('documents.terminationDate')} *
              </label>
              <input
                type="date"
                name="terminationDate"
                value={formData.terminationDate}
                onChange={handleInputChange}
                className="input"
                required
              />
              <p className="text-sm text-gray-600 mt-1">
                {t('documents.terminationDateWillBeSaved')}
              </p>
            </div>
          );
        } else {
          // If employee has employmentEndDate, show info
          return (
            <div className="mb-6">
              <label className="label">
                {t('documents.terminationDate')}
              </label>
              <div className="p-3 bg-green-50 rounded-md">
                <span className="text-green-600">
                  {t('documents.employmentEndDateSet')}: {new Date(selectedEmployee.employmentEndDate).toLocaleDateString('de-DE')}
                </span>
              </div>
            </div>
          );
        }
      })()}

            {/* Employment Start Date - Only for fristgerechte_kündigung and if employee doesn't have employmentStartDate */}
            {formData.documentType === 'fristgerechte_kündigung' && formData.employeeId && (() => {
              const selectedEmployee = employees.find(emp => 
                (emp._id === formData.employeeId) || 
                (emp._id === formData.employeeId._id)
              );
              
              if (!selectedEmployee) return null;
              
              if (selectedEmployee.employmentStartDate) {
                return (
                  <div className="mb-6">
                    <label className="label">
                      {t('documents.employmentStartDate')}
                    </label>
                    <div className="p-3 bg-gray-50 rounded border">
                      <span className="text-green-600">
                        {t('documents.employmentStartDateSet')}: {new Date(selectedEmployee.employmentStartDate).toLocaleDateString('de-DE')}
                      </span>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="mb-6">
                    <label className="label">
                      {t('documents.employmentStartDate')} *
                    </label>
                    <input
                      type="date"
                      name="employmentStartDate"
                      value={formData.employmentStartDate}
                      onChange={handleInputChange}
                      className="input"
                      required
                    />
                    <p className="text-sm text-gray-600 mt-1">
                      {t('documents.employmentStartDateWillBeSaved')}
                    </p>
                  </div>
                );
              }
            })()}

            {/* Employment Leftover Vacation Days - Only for termination of employment for business reasons */}
            {formData.documentType === 'kündigung_betriebliche_gründe' && (
              <div className="mb-6">
                <label className="label">
                  {t('documents.employmentLeftoverVacationDays')} *
                </label>
                <input
                  type="number"
                  name="employmentLeftoverVacationDays"
                  value={formData.employmentLeftoverVacationDays}
                  onChange={handleInputChange}
                  className="input"
                  placeholder={t('documents.employmentLeftoverVacationDaysPlaceholder')}
                  required
                  min="0"
                />
                <p className="text-sm text-gray-600 mt-1">
                  {t('documents.employmentLeftoverVacationDaysHelp')}
                </p>
              </div>
            )}

            {/* Sender Name - Only for termination documents */}
            {(formData.documentType === 'außerordentliche_kündigung' || formData.documentType === 'kündigung_betriebliche_gründe' || formData.documentType === 'beendigung_vertragsablauf' || formData.documentType === 'fristgerechte_kündigung' || formData.documentType === 'arbeitsaufforderung') && (
              <div className="mb-6">
                <label className="label">
                  {t('documents.senderName')} *
                </label>
                <input
                  type="text"
                  name="senderName"
                  value={formData.senderName}
                  onChange={handleInputChange}
                  className="input"
                  placeholder={t('documents.senderNamePlaceholder')}
                  required
                />
              </div>
            )}

            {/* Absence Start Date - Only for work request */}
            {formData.documentType === 'arbeitsaufforderung' && (
              <div className="mb-6">
                <label className="label">
                  {t('documents.absenceStartDate')} *
                </label>
                <input
                  type="date"
                  name="absenceStartDate"
                  value={formData.absenceStartDate}
                  onChange={handleInputChange}
                  className="input"
                  required
                />
                <p className="text-sm text-gray-600 mt-1">
                  {t('documents.absenceStartDateHelp')}
                </p>
              </div>
            )}

            {/* Absence Deadline Date - Only for work request */}
            {formData.documentType === 'arbeitsaufforderung' && (
              <div className="mb-6">
                <label className="label">
                  {t('documents.absenceDeadlineDate')} *
                </label>
                <input
                  type="date"
                  name="absenceDeadlineDate"
                  value={formData.absenceDeadlineDate}
                  onChange={handleInputChange}
                  className="input"
                  required
                />
                <p className="text-sm text-gray-600 mt-1">
                  {t('documents.absenceDeadlineDateHelp')}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingDocument(null);
                  resetForm();
                }}
                className="btn btn-secondary"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveDocument}
                disabled={saveMutation.isPending}
                className="btn btn-primary"
              >
                {saveMutation.isPending ? t('common.saving') : (editingDocument ? t('common.update') : t('common.save'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StandardDocuments;